import React, { useState, useEffect } from 'react';
import { stockInwardAPI, propertyAPI, storeAPI, vendorAPI, itemAPI, unitAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, ArrowDownToLine, Search, Loader2, Trash2, Eye, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatNumber, formatCurrency } from '../lib/utils';

export const StockInwardPage = () => {
  const [records, setRecords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [stores, setStores] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterStore, setFilterStore] = useState('all');
  const [formData, setFormData] = useState({
    property_id: '',
    store_id: '',
    vendor_id: '',
    invoice_number: '',
    invoice_date: '',
    items: [{ item_id: '', quantity: '', unit_id: '', rate: '' }],
    notes: ''
  });
  const [editFormData, setEditFormData] = useState({
    vendor_id: '',
    invoice_number: '',
    invoice_date: '',
    quantity: '',
    rate: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recordsRes, propertiesRes, storesRes, vendorsRes, itemsRes, unitsRes] = await Promise.all([
        stockInwardAPI.getAll(),
        propertyAPI.getAll({ status: 'active' }),
        storeAPI.getAll({ status: 'active' }),
        vendorAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' })
      ]);
      setRecords(recordsRes.data);
      setProperties(propertiesRes.data);
      setStores(storesRes.data);
      setVendors(vendorsRes.data);
      setItems(itemsRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = () => {
    setFormData({
      property_id: properties[0]?.id || '',
      store_id: '',
      vendor_id: '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      items: [{ item_id: '', quantity: '', unit_id: '', rate: '' }],
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { item_id: '', quantity: '', unit_id: '', rate: '' }]
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Auto-fill unit when item is selected
    if (field === 'item_id') {
      const selectedItem = items.find(i => i.id === value);
      if (selectedItem?.default_unit_id) {
        newItems[index].unit_id = selectedItem.default_unit_id;
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate items
    const validItems = formData.items.filter(item => item.item_id && item.quantity);
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      setSaving(true);
      const data = {
        ...formData,
        items: validItems.map(item => ({
          item_id: item.item_id,
          quantity: parseFloat(item.quantity) || 0,
          unit_id: item.unit_id,
          rate: parseFloat(item.rate) || 0,
          amount: (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)
        }))
      };
      
      await stockInwardAPI.create(data);
      toast.success('Stock inward recorded successfully');
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEditDialog = (record) => {
    if (record.grn_id) {
      toast.error('Cannot edit GRN-created entries. Please modify via GRN.');
      return;
    }
    setSelectedRecord(record);
    setEditFormData({
      vendor_id: record.vendor_id || '',
      invoice_number: record.invoice_number || '',
      invoice_date: record.invoice_date?.split('T')[0] || '',
      quantity: record.quantity?.toString() || '',
      rate: record.rate?.toString() || '',
      notes: record.notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      setSaving(true);
      const updateData = {
        vendor_id: editFormData.vendor_id || null,
        invoice_number: editFormData.invoice_number || null,
        invoice_date: editFormData.invoice_date || null,
        quantity: editFormData.quantity ? parseFloat(editFormData.quantity) : null,
        rate: editFormData.rate ? parseFloat(editFormData.rate) : null,
        notes: editFormData.notes || null
      };
      
      await stockInwardAPI.update(selectedRecord.id, updateData);
      toast.success('Stock inward updated successfully');
      setEditDialogOpen(false);
      setSelectedRecord(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update record');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRecord) return;
    
    try {
      setSaving(true);
      await stockInwardAPI.delete(selectedRecord.id);
      toast.success('Stock inward entry deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete record');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (record) => {
    if (record.grn_id) {
      toast.error('Cannot delete GRN-created entries. Please cancel the GRN instead.');
      return;
    }
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const filteredRecords = records.filter(r => {
    const matchesSearch = r.transaction_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesProperty = filterProperty === 'all' || r.property_id === filterProperty;
    const matchesStore = filterStore === 'all' || r.store_id === filterStore;
    return matchesSearch && matchesProperty && matchesStore;
  });

  // Filter stores based on selected property
  const filteredStores = formData.property_id
    ? stores.filter(s => s.property_id === formData.property_id)
    : stores;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Stock Inward</h1>
          <p className="text-muted-foreground mt-1">Record incoming stock and purchases</p>
        </div>
        <Button onClick={handleOpenDialog} className="rounded-full" data-testid="add-stock-inward-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Inward Entry
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by transaction or invoice number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-inward-input"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterProperty} onValueChange={setFilterProperty}>
                <SelectTrigger className="w-[150px]" data-testid="filter-inward-property">
                  <SelectValue placeholder="Property" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Properties</SelectItem>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStore} onValueChange={setFilterStore}>
                <SelectTrigger className="w-[150px]" data-testid="filter-inward-store">
                  <SelectValue placeholder="Store" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {stores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner h-8 w-8" />
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <ArrowDownToLine className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <p className="text-lg font-medium text-foreground">No inward records found</p>
              <p className="text-muted-foreground">Start by recording your first stock inward</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transaction #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Store</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Invoice #</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.map((record) => (
                    <TableRow key={record.id} data-testid={`inward-row-${record.id}`}>
                      <TableCell className="font-medium font-mono">{record.transaction_number}</TableCell>
                      <TableCell>{formatDate(record.date)}</TableCell>
                      <TableCell>{record.property_name || '-'}</TableCell>
                      <TableCell>{record.store_name || '-'}</TableCell>
                      <TableCell>{record.vendor_name || '-'}</TableCell>
                      <TableCell>{record.invoice_number || '-'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(record.total_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedRecord(record);
                            setViewDialogOpen(true);
                          }}
                          data-testid={`view-inward-${record.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {!record.grn_id && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenEditDialog(record)}
                              data-testid={`edit-inward-${record.id}`}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDeleteDialog(record)}
                              data-testid={`delete-inward-${record.id}`}
                              title="Delete"
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Stock Inward</DialogTitle>
            <DialogDescription>
              Update the stock inward entry details
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Vendor</Label>
              <Select
                value={editFormData.vendor_id || "none"}
                onValueChange={(v) => setEditFormData({ ...editFormData, vendor_id: v === "none" ? "" : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Vendor</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Invoice Number</Label>
                <Input
                  value={editFormData.invoice_number}
                  onChange={(e) => setEditFormData({ ...editFormData, invoice_number: e.target.value })}
                  placeholder="Invoice #"
                />
              </div>
              <div className="space-y-2">
                <Label>Invoice Date</Label>
                <Input
                  type="date"
                  value={editFormData.invoice_date}
                  onChange={(e) => setEditFormData({ ...editFormData, invoice_date: e.target.value })}
                />
              </div>
            </div>
            {selectedRecord?.quantity !== undefined && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    value={editFormData.quantity}
                    onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                    placeholder="Quantity"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rate (₹)</Label>
                  <Input
                    type="number"
                    value={editFormData.rate}
                    onChange={(e) => setEditFormData({ ...editFormData, rate: e.target.value })}
                    placeholder="Rate"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditSubmit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Delete Stock Inward Entry</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this entry? This will also reverse the stock quantity. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Stock Inward Entry</DialogTitle>
            <DialogDescription>
              Record incoming stock from vendors or purchases
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Property *</Label>
                  <Select
                    value={formData.property_id}
                    onValueChange={(value) => setFormData({ ...formData, property_id: value, store_id: '' })}
                  >
                    <SelectTrigger data-testid="inward-property-select">
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Store *</Label>
                  <Select
                    value={formData.store_id}
                    onValueChange={(value) => setFormData({ ...formData, store_id: value })}
                  >
                    <SelectTrigger data-testid="inward-store-select">
                      <SelectValue placeholder="Select store" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredStores.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Vendor</Label>
                  <Select
                    value={formData.vendor_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, vendor_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="inward-vendor-select">
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Vendor</SelectItem>
                      {vendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Invoice Number</Label>
                  <Input
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    data-testid="inward-invoice-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Invoice Date</Label>
                  <Input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    data-testid="inward-date-input"
                  />
                </div>
              </div>

              {/* Items Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Items *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddItem} data-testid="add-inward-item-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30%]">Item</TableHead>
                        <TableHead className="w-[15%]">Quantity</TableHead>
                        <TableHead className="w-[20%]">Unit</TableHead>
                        <TableHead className="w-[15%]">Rate</TableHead>
                        <TableHead className="w-[15%]">Amount</TableHead>
                        <TableHead className="w-[5%]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => {
                        const amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0);
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Select
                                value={item.item_id || "none"}
                                onValueChange={(value) => handleItemChange(index, 'item_id', value === "none" ? "" : value)}
                              >
                                <SelectTrigger data-testid={`inward-item-${index}-select`}>
                                  <SelectValue placeholder="Select item" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Select item</SelectItem>
                                  {items.map((i) => (
                                    <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                data-testid={`inward-qty-${index}-input`}
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={item.unit_id || "none"}
                                onValueChange={(value) => handleItemChange(index, 'unit_id', value === "none" ? "" : value)}
                              >
                                <SelectTrigger data-testid={`inward-unit-${index}-select`}>
                                  <SelectValue placeholder="Unit" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Select unit</SelectItem>
                                  {units.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.rate}
                                onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                                data-testid={`inward-rate-${index}-input`}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatCurrency(amount)}
                            </TableCell>
                            <TableCell>
                              {formData.items.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveItem(index)}
                                  data-testid={`remove-inward-item-${index}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="inward-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-inward-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Stock Inward Details</DialogTitle>
            <DialogDescription>
              Transaction: {selectedRecord?.transaction_number}
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(selectedRecord.date)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Property</p>
                  <p className="font-medium">{selectedRecord.property_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Store</p>
                  <p className="font-medium">{selectedRecord.store_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Vendor</p>
                  <p className="font-medium">{selectedRecord.vendor_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Invoice #</p>
                  <p className="font-medium">{selectedRecord.invoice_number || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Entered By</p>
                  <p className="font-medium">{selectedRecord.entered_by_name}</p>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRecord.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-right">{formatNumber(item.quantity)}</TableCell>
                        <TableCell>{item.unit_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.rate)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} className="text-right font-semibold">Total</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(selectedRecord.total_amount)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              {selectedRecord.notes && (
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  <p className="text-sm">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockInwardPage;
